// lineup-data.js - pure helpers for the coach-page Line-up manager (Module:
// participation, a STANDALONE lens - never a Dragons Score input, director
// decision 2026-06-29). Formations + slot geometry are ported verbatim from
// the khc-tactical-diagrams skill so the in-app field matches the branded PNG
// line-ups exactly. No DOM, no network - Vitest covers these directly.

// Formation -> players per line, GK first. Groups: 11-a-side (Bovenbouw),
// 8-a-side (Middenbouw), and 6-a-side (young 6v6, e.g. U9). Same shapes the
// tactical-diagrams skill offers, plus the small-sided set for the young end.
export const FORMATIONS = {
  // 11-a-side
  '1-4-2-2-2': [1, 4, 2, 2, 2],
  '1-3-2-2-3': [1, 3, 2, 2, 3],
  '1-5-2-3':   [1, 5, 2, 3],
  '1-4-3-3':   [1, 4, 3, 3],
  // 8-a-side
  '1-3-2-2':   [1, 3, 2, 2],
  '1-3-3-1':   [1, 3, 3, 1],
  '1-3-1-3':   [1, 3, 1, 3],
  '1-4-2-1':   [1, 4, 2, 1],
  // 6-a-side (keeper + 5 outfield): young 6v6
  '1-2-3':     [1, 2, 3],
  '1-3-2':     [1, 3, 2],
  '1-2-2-1':   [1, 2, 2, 1],
  // 3-a-side (no keeper): U7-U8 3v3 on small goals. Lines are ALL outfield,
  // back-to-front; see KEEPERLESS below - these skip the goalkeeper geometry.
  '2-1':       [2, 1],
  '1-2':       [1, 2],
};

// Formations with no goalkeeper: every line is an outfield line spread across
// the field, rather than line 0 sitting in the keeper's spot on the backline.
export const KEEPERLESS = new Set(['2-1', '1-2']);

export const FORMATION_GROUPS = [
  { size: 11, label: '11-a-side',          keys: ['1-4-2-2-2', '1-3-2-2-3', '1-5-2-3', '1-4-3-3'] },
  { size: 8,  label: '8-a-side',           keys: ['1-3-2-2', '1-3-3-1', '1-3-1-3', '1-4-2-1'] },
  { size: 6,  label: '6-a-side',           keys: ['1-2-3', '1-3-2', '1-2-2-1'] },
  { size: 3,  label: '3-a-side (no keeper)', keys: ['2-1', '1-2'] },
];

/** How many players a formation fields (incl. GK). */
export function formationSize(formation) {
  const lines = FORMATIONS[formation];
  return lines ? lines.reduce((s, n) => s + n, 0) : 0;
}

/**
 * The slot geometry for a formation - one entry per starting position, in the
 * order players fill them (GK, then back-to-front by line). x/y are 0-100
 * percentages of a vertical field (GK on the backline at the bottom, attack at
 * the top), identical to the skill's `lineup_objects` math. Each slot carries:
 *   lineRole - Goalkeeper / Defence / Midfield / Attack (for the GK badge)
 *   line     - Defence (GK folds in here) / Midfield / Forward   <- stored
 *   position - Left / Middle / Right, from the x coordinate       <- stored
 */
// Wide players are lifted UP the field relative to the centre of their line,
// so their name boxes never cross a neighbour's (director request 2026-07-13).
const STAGGER = 7;

export function slotPositions(formation) {
  const lines = FORMATIONS[formation];
  if (!lines) return [];
  const hasGK = !KEEPERLESS.has(formation);
  const L = lines.length;
  const yGK = 91, yBack = 80, yTop = 15;
  const out = [];
  let k = 0;
  lines.forEach((n, i) => {
    // With a keeper, line 0 sits deep in the goal and the outfield lines fan up
    // from yBack. Keeperless: every line is outfield, fanned yBack -> yTop.
    const baseY = hasGK
      ? (i === 0 ? yGK : (L - 1 <= 1 ? 50 : yBack - (i - 1) * (yBack - yTop) / (L - 2)))
      : (L <= 1 ? 50 : yBack - i * (yBack - yTop) / (L - 1));
    const gap = Math.min(24, 76 / n);
    const maxCentred = (n - 1) / 2;
    for (let j = 0; j < n; j++) {
      const centred = j - (n - 1) / 2;
      const x = n === 1 ? 50 : 50 + centred * gap;
      // The wider the player (further from the centre), the higher up they sit.
      // The keeper never staggers; an outfield line always can (incl. a
      // keeperless line 0).
      const isGKLine = hasGK && i === 0;
      const stagger = (isGKLine || maxCentred === 0) ? 0 : STAGGER * (Math.abs(centred) / maxCentred);
      const lineRole = hasGK ? _lineRole(i, L) : _lineRoleNoGK(i, L);
      out.push({
        index: k, x, y: baseY - stagger, line: i, lineRole, n, gap,
        lineName: _lineName(lineRole),
        position: x < 40 ? 'Left' : x > 60 ? 'Right' : 'Middle',
      });
      k += 1;
    }
  });
  return out;
}

function _lineRole(lineIndex, lineCount) {
  if (lineIndex === 0) return 'Goalkeeper';
  if (lineIndex === 1) return 'Defence';
  if (lineIndex === lineCount - 1) return 'Attack';
  return 'Midfield';
}

// Keeperless variant: line 0 is the outfield back line (not a goalkeeper).
function _lineRoleNoGK(lineIndex, lineCount) {
  if (lineCount <= 1) return 'Midfield';
  if (lineIndex === 0) return 'Defence';
  if (lineIndex === lineCount - 1) return 'Attack';
  return 'Midfield';
}
// Fold the four render roles into the three stored LINE buckets (GK -> Defence).
function _lineName(lineRole) {
  if (lineRole === 'Attack') return 'Forward';
  if (lineRole === 'Midfield') return 'Midfield';
  return 'Defence'; // Goalkeeper + Defence
}

/** Representative y (% of field) for each LINE in a formation - used to place
 *  a bench player's box at the side of the line they will come on in. */
export function lineYs(formation) {
  const byLine = {};
  slotPositions(formation).forEach(s => {
    (byLine[s.lineName] ||= []).push(s.y);
  });
  const out = {};
  Object.entries(byLine).forEach(([ln, ys]) => { out[ln] = ys.reduce((a, b) => a + b, 0) / ys.length; });
  return out;
}

/**
 * Aggregate a player's participation across matches. Input: rows of
 * { player_id, status, line } (one per player per match). Returns a Map
 * player_id -> { matches, starts, subs, out, played, byRole: {Line: count} }
 * where `played` = starts + subs and byRole tallies the LINE they started in.
 */
export function participationRollup(spots) {
  const out = new Map();
  (spots || []).forEach(s => {
    if (!s || !s.player_id) return;
    if (!out.has(s.player_id)) {
      out.set(s.player_id, { matches: 0, starts: 0, subs: 0, out: 0, played: 0, byRole: {} });
    }
    const p = out.get(s.player_id);
    p.matches += 1;
    if (s.status === 'start') { p.starts += 1; p.played += 1; }
    else if (s.status === 'sub') { p.subs += 1; p.played += 1; }
    else p.out += 1;
    if (s.status === 'start' && s.line) {
      p.byRole[s.line] = (p.byRole[s.line] || 0) + 1;
    }
  });
  return out;
}

/** The player's most-frequent starting line, or null if they never started. */
export function mainRole(rollupEntry) {
  const roles = Object.entries(rollupEntry?.byRole || {});
  if (!roles.length) return null;
  return roles.sort((a, b) => b[1] - a[1])[0][0];
}
